import React, {
	useState, useMemo, useEffect, useRef,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import classNames from 'classnames';
import TellyCard from '../../../../images/telly-card.png';
import Socket from '../../../../connection/socket';
import { GetRequiresApproval, CheckJoinRequest } from '../../../../connection/join-request';
import Settings from '../../../../socket-listeners/settings';
import Broadcast from '../../../../socket-listeners/broadcasting';
import HostSync from '../../../../socket-listeners/host-sync';
import Shots from '../../../../socket-listeners/shots';
import Users from '../../../../socket-listeners/users';
import Graphics from '../../../../socket-listeners/graphics';
import { GreenroomState as GS } from '../../../../stores/types/types';
import { setSettingsReducerSocket } from '../../../../stores/reducers/settings';
import { SHOT_THUMBNAIL_TYPES } from '../../../../stores/types/shots';
import { setShotReducerSocket } from '../../../../stores/reducers/shots';
import * as SessionActions from '../../../../stores/actions/session';
import * as InvitationActions from '../../../../stores/actions/invitations';
import * as HomeActions from '../../../../stores/actions/home';
import { showDetailsBack } from '../../../../stores/actions/main';
import { cardBackground } from '../../../../utils/utils';
import WaitingIndicator from '../../../general-components/waiting-indicator';
import Icon, { ICONS, COLORS } from '../../../general-components/icon';
import EventCard from '../../../general-components/event-card';
import ShowDetailsInfo from './show-details-info';
import ShowDetailsSettings from './show-details-settings';
import ShotThumbnail from '../../../general-components/shot-thumbnail';
import LoadingSpinner from '../../../general-components/loading-spinner';
import useHome from '../../../../hooks/use-home';
import useJoin from '../../../../hooks/use-join';
import useLoad from '../../../../hooks/use-load';
import usePrevious from '../../../../utils/use-previous';
import { getBroadcastServer } from '../../../../stores/actions/broadcasting';

interface Props {
	connected: boolean;
	socket: Socket | null;
}

const SHOW_DETAILS_ROUTES = {
	INFO: 0,
	RECORDINGS: 1,
	SETTINGS: 2,
};

export default function ShowDetails(props: Props) {
	const host = useSelector((s: GS) => s.SessionReducer.host);
	const token = useSelector((s: GS) => s.SessionReducer.token);
	const selectedEvent = useSelector((s: GS) => s.SessionReducer.selectedEvent);
	const showSessions = useSelector((s: GS) => s.SessionReducer.showSessions);
	const showSessionTypes = useSelector((s: GS) => s.SessionReducer.showSessionTypes);
	const showSessionsLoading = useSelector((s: GS) => s.SessionReducer.showSessionsLoading);
	const awaitingProducer = useSelector((s: GS) => s.SessionReducer.awaitingProducer);
	const liveChannels = useSelector((s: GS) => s.ContentReducer.liveChannels);
	const events = useSelector((s: GS) => s.ContentReducer.events);
	const invitations = useSelector((s: GS) => s.InvitationsReducer.invitations);
	const joinRequest = useSelector((s: GS) => s.InvitationsReducer.joinRequest);
	const homeMeeting = useSelector((s: GS) => s.HomeReducer.homeMeeting);
	const selectedChannel = useSelector((s: GS) => s.HomeReducer.selectedChannel);
	const shots = useSelector((s: GS) => s.ShotsReducer.shots);
	const shotsLoaded = useSelector((s: GS) => s.ShotsReducer.shotsLoaded);
	const hasFetchedFFOverrides = useSelector((s: GS) => s.SettingsReducer.hasFetchedFFOverrides);
	const { use_spielberg: useSpielberg } = useSelector((s: GS) => s.SettingsReducer.featureFlags);

	const { connected } = props;
	const dispatch = useDispatch();
	const { filteredMeetingsByChannel } = useHome();
	const { joinGreenroom, enterGreenroom } = useJoin();
	const [detailsRoute, setDetailsRoute] = useState<number>(SHOW_DETAILS_ROUTES.INFO);
	const [currentShot, setCurrentShot] = useState<number>(0);
	const joinRequestCheckInverval = useRef<NodeJS.Timeout | null>(null);
	const settingSocket = useRef<Socket | null>(null);

	const { loadShowDetails } = useLoad();

	async function checkJoinRequest(request: number) {
		if (request) {
			const {
				approval_required: approvalRequired,
			} = await GetRequiresApproval(token!, selectedEvent.meeting);
			if (!approvalRequired) dispatch(SessionActions.setAwaitingProducer(false));
			const { approved } = await CheckJoinRequest(token!, selectedEvent.meeting);
			if (approved) {
				if (joinRequestCheckInverval.current) clearInterval(joinRequestCheckInverval.current);
				enterGreenroom(selectedEvent);
			}
		}
	}

	useEffect(() => {
		if (!hasFetchedFFOverrides) {
			return;
		}

		if (token && homeMeeting?.meeting) {
			settingSocket.current = new Socket(`meeting-${homeMeeting?.meeting}`);

			settingSocket.current.onReconnected = async () => {
				loadShowDetails();
			};

			Settings(settingSocket.current);
			Shots(settingSocket.current);
			setShotReducerSocket(settingSocket.current);
			Users(settingSocket.current);
			Graphics(settingSocket.current);
			settingSocket.current.getUsers();

			if (host) {
				setSettingsReducerSocket(settingSocket.current);
				Broadcast(settingSocket.current);
				HostSync(settingSocket.current);
			}

			loadShowDetails();
		} else {
			dispatch(InvitationActions.clearInvitations());
		}
	}, [hasFetchedFFOverrides]);

	// get or provision a Bullman server if we turn the Spielberg feature flag from on to off
	const prevUseSpielberg = usePrevious(useSpielberg);
	useEffect(() => {
		if (prevUseSpielberg && !useSpielberg && token && homeMeeting) {
			dispatch(getBroadcastServer(token, homeMeeting.meeting));
		}
	}, [
		useSpielberg,
		prevUseSpielberg,
		token,
		homeMeeting,
	]);

	useEffect(() => {
		// if a presenter is on the details page and their invite is revoked,
		// return them to home since they shouldn't be able to access this page anymore
		if (
			!host
			&& !filteredMeetingsByChannel().some((event: any) => event.meeting === homeMeeting?.meeting)
		) {
			dispatch(showDetailsBack());
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [dispatch, events, selectedChannel, homeMeeting?.meeting]);

	useEffect(
		() => () => {
			if (joinRequestCheckInverval.current) {
				clearInterval(joinRequestCheckInverval.current);
				dispatch(InvitationActions.setJoinRequest(null));
			}
			settingSocket.current?.disconnect();
			dispatch(HomeActions.setHomeMeeting(null));
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[],
	);

	useEffect(() => {
		// forcing the join request to poll AND wait for websocket
		// in case the websocket connection is spotty or slow
		if (joinRequest) {
			joinRequestCheckInverval.current = setInterval(() => checkJoinRequest(joinRequest), 3000);
		}

		return () => {
			if (joinRequestCheckInverval.current) clearInterval(joinRequestCheckInverval.current);
		};
	}, [joinRequest]);

	useEffect(() => {
		// producer dropped while awaiting join request
		if (selectedEvent && !liveChannels.includes(selectedEvent.meeting)) {
			if (selectedEvent.entry_without_producer && joinRequestCheckInverval.current) return;
			dispatch(SessionActions.setAwaitingProducer(false));
		}
	}, [liveChannels, selectedEvent]);

	useEffect(() => {
		if (awaitingProducer && !joinRequest) {
			dispatch(SessionActions.setAwaitingProducer(false));
		}
	}, [awaitingProducer, dispatch, joinRequest]);

	const eventInfo = useMemo(() => (
			<div className="show-details-event-info">
				<EventCard
					connected={connected}
					event={homeMeeting}
					joinGreenroom={joinGreenroom}
					isWaiting={awaitingProducer}
					showSessionType={'Broadcast'}
				/>
			</div>
	), [homeMeeting, liveChannels, connected, awaitingProducer]);

	return (
		<div className="show-details-wrapper">
			<div className="show-details-above-content">
				<div className="showboard" style={!homeMeeting?.meeting || !(shots.length > 0) ? cardBackground(homeMeeting, TellyCard, true, true) : undefined}>
					{homeMeeting?.meeting && shots.length > 0 && (
						!shotsLoaded ? (
							<div className="loading-container">
								<WaitingIndicator />
							</div>
						) : (
							<>
								<div className="shot-thumbnail-wrapper">
									<ShotThumbnail
										borderRatio={0.7}
										shot={shots[currentShot]}
										shotThumbnailType={SHOT_THUMBNAIL_TYPES.SHOW_DETAILS}
									/>
								</div>
								<div className="showboard-controls">
									<div className="showboard-buttons">
										<button onClick={() => setCurrentShot(currentShot - 1)} disabled={!currentShot}>
											<Icon name={ICONS.KEYBOARD_ARROW_LEFT} size={10} color={COLORS.WHITE} />
										</button>
										<div className="current-shot-container">
											<span className="current-shot">{currentShot + 1}&nbsp;</span>
											<span>/ {shots.length}</span>
										</div>
										<button
											onClick={() => setCurrentShot(currentShot + 1)}
											disabled={currentShot === shots.length - 1}
										>
											<Icon name={ICONS.KEYBOARD_ARROW_RIGHT} size={10} color={COLORS.WHITE} />
										</button>
									</div>
								</div>
							</>
						)
					)}
				</div>
				{homeMeeting && eventInfo}
			</div>
			<div className="show-details-below-content">
				<div className="show-details-menu">
					<button className={classNames({ selected: detailsRoute === SHOW_DETAILS_ROUTES.INFO }, 'background-dark round')}
						onClick={() => setDetailsRoute(SHOW_DETAILS_ROUTES.INFO)}>
						<div className='width-full padding-left-large align-items-center justify-content-left'>
							<Icon
								name={ICONS.INFO_OUTLINE}
								color={
									detailsRoute === SHOW_DETAILS_ROUTES.INFO
										? COLORS.WHITE
										: COLORS.DEFAULT_GRAY
								}
								size={14}
							/>
							<span>Info</span>
						</div>
					</button>
					{host
						&& <button className={classNames({ selected: detailsRoute === SHOW_DETAILS_ROUTES.SETTINGS }, 'background-dark round')}
							onClick={() => setDetailsRoute(SHOW_DETAILS_ROUTES.SETTINGS)}
							disabled={!homeMeeting?.meeting}>
							<div className='width-full padding-left-large align-items-center justify-content-left'>
								<Icon
									name={ICONS.SETTINGS}
									color={
										detailsRoute === SHOW_DETAILS_ROUTES.SETTINGS
											? COLORS.WHITE
											: COLORS.DEFAULT_GRAY
									}
									size={14}
								/>
								<span>Settings</span>
							</div>
						</button>
					}
				</div>
				{homeMeeting && (
					<div className="show-details-section-container">
						{detailsRoute === SHOW_DETAILS_ROUTES.INFO && (
							!showSessionsLoading && showSessionTypes.length > 0
								? <ShowDetailsInfo
									connected={connected}
									homeMeeting={homeMeeting}
									invitations={invitations}
									showSessions={showSessions}
									showSessionTypes={showSessionTypes}
									joinGreenroom={joinGreenroom}
									socket={settingSocket.current}
								/>
								: <LoadingSpinner></LoadingSpinner>
						)}
						{detailsRoute === SHOW_DETAILS_ROUTES.SETTINGS
							&& <ShowDetailsSettings
								homeMeeting={homeMeeting}
								invitations={invitations}
								socket={settingSocket.current}
							/>
						}
					</div>
				)}
			</div>
		</div>
	);
}
